import { AlertCircle, CheckCircle2, Loader2, MinusCircle } from 'lucide-react'
import { useSourceRuns } from '../api'
import type { SourceRunView } from '../types'

/**
 * What each sync attempt actually did.
 *
 * Rows read against rows inserted is the column pair that matters, and the reason this
 * table exists at all. A replay that reports "read 1,240, inserted 0" is working exactly as
 * intended; a scheduled poll reporting the same thing is a source that has stopped
 * producing. A single "imported" count cannot tell those apart, and that was the only
 * number available before.
 */
export function SourceRunsTable({ sourceId }: { sourceId: string }) {
  const { data: runs, isPending } = useSourceRuns(sourceId)

  if (isPending) {
    return <p className="p-4 text-sm text-[var(--color-muted)]">Loading sync history…</p>
  }
  if (!runs || runs.length === 0) {
    return (
      <p className="p-4 text-sm text-[var(--color-muted)]">
        This source has not run yet. Use Sync now to read from it immediately.
      </p>
    )
  }

  return (
    <div className="overflow-x-auto overscroll-x-contain">
      <table className="w-full min-w-[46rem] text-sm">
        <thead>
          <tr className="border-b border-[var(--color-border)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
            <th scope="col" className="py-2 pr-3 font-medium">Started</th>
            <th scope="col" className="py-2 pr-3 font-medium">Trigger</th>
            <th scope="col" className="py-2 pr-3 text-right font-medium">Read</th>
            <th scope="col" className="py-2 pr-3 text-right font-medium">New</th>
            <th scope="col" className="py-2 pr-3 text-right font-medium">Already stored</th>
            <th scope="col" className="py-2 pr-3 text-right font-medium">Unmatched</th>
            <th scope="col" className="py-2 pr-3 font-medium">Watermark</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((run) => (
            <tr key={run.id} className="border-b border-[var(--color-border)] last:border-0">
              <td className="py-2 pr-3 whitespace-nowrap">
                <span className="inline-flex items-center gap-1.5">
                  <RunStatusIcon status={run.status} />
                  {new Date(run.startedAt).toLocaleString()}
                </span>
                {run.message && (
                  <span className="block text-xs text-[var(--color-muted)]">{run.message}</span>
                )}
              </td>
              <td className="py-2 pr-3 whitespace-nowrap">
                {run.trigger === 'REPLAY' && run.replayFrom
                  ? `Replay ${run.replayFrom} → ${run.replayTo}`
                  : run.trigger.charAt(0) + run.trigger.slice(1).toLowerCase()}
              </td>
              <td className="py-2 pr-3 text-right tabular-nums">{run.rowsRead}</td>
              <td className="py-2 pr-3 text-right font-medium tabular-nums">{run.rowsInserted}</td>
              <td className="py-2 pr-3 text-right tabular-nums text-[var(--color-muted)]">
                {run.rowsDuplicate}
              </td>
              <td className="py-2 pr-3 text-right tabular-nums">
                {run.rowsUnmatched > 0 ? (
                  <span className="text-[var(--color-warning)]">{run.rowsUnmatched}</span>
                ) : (
                  <span className="text-[var(--color-muted)]">0</span>
                )}
              </td>
              <td className="py-2 pr-3 text-xs text-[var(--color-muted)]">
                {run.watermarkBefore === run.watermarkAfter ? (
                  // Unchanged is the important case, not a boring one: it is what proves a
                  // failed run lost nothing and a replay rewound nothing.
                  <span title="Unchanged by this run">{run.watermarkAfter ?? '—'}</span>
                ) : (
                  <span>
                    {run.watermarkBefore ?? '—'} → <strong>{run.watermarkAfter ?? '—'}</strong>
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function RunStatusIcon({ status }: { status: SourceRunView['status'] }) {
  const shared = 'size-4 shrink-0'
  if (status === 'OK') {
    return <CheckCircle2 className={`${shared} text-[var(--color-success)]`} aria-label="Succeeded" />
  }
  if (status === 'FAILED') {
    return <AlertCircle className={`${shared} text-[var(--color-danger)]`} aria-label="Failed" />
  }
  if (status === 'RUNNING') {
    return <Loader2 className={`${shared} animate-spin text-[var(--color-muted)]`} aria-label="Running" />
  }
  return <MinusCircle className={`${shared} text-[var(--color-muted)]`} aria-label="Skipped" />
}
